/**
 * WHICH CERTIFICATES THE SCREEN MAY OFFER A FIRST E-MAIL FOR.
 *
 * The server decides; this exists so the screen says the same thing about the same record. Offering
 * an action the server will refuse is worse than offering nothing: the operator selects ninety
 * records, presses, and is told that eleven of them were never eligible — which they could have
 * been shown before pressing.
 *
 * So this is a copy of `backend/src/domain/email/first-send.ts`, deliberately, and the two are held
 * together by `tests/fixtures/first-send-eligibility.json`: each package runs its own
 * implementation against every vector in that file, so a rule added on one side fails on the other
 * until it is learned there too. The reason sentences the product shows come from the same file.
 */
export const FIRST_SEND_DECISIONS = [
  'eligible',
  'already_has_email',
  'previous_attempt_failed',
  'no_recipient_email',
  'recipient_email_invalid',
  'awaiting_admin_publish',
  'declined',
  'revoked',
  'superseded',
  'expired',
] as const;

export type FirstSendDecision = (typeof FIRST_SEND_DECISIONS)[number];
export type FirstSendMessage = 'claim' | 'notice';

export interface FirstSendFacts {
  viewState: string;
  recipientEmail: string | null;
  deliveries: readonly string[];
}

export interface FirstSendVerdict {
  decision: FirstSendDecision;
  message?: FirstSendMessage;
}

/** The statuses that mean an e-mail exists. `failed` has its own answer; see the server's note. */
const EXISTS: ReadonlySet<string> = new Set([
  'queued',
  'sending',
  'sent',
  'delivered',
  'soft_bounced',
  'hard_bounced',
  'suppressed',
]);

const normalize = (email: string): string =>
  String(email ?? '')
    .trim()
    .toLowerCase();
const plausible = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email.length <= 320;

export function decideFirstSend(facts: FirstSendFacts): FirstSendVerdict {
  switch (facts.viewState) {
    case 'pending_admin_publish':
      return { decision: 'awaiting_admin_publish' };
    case 'declined':
      return { decision: 'declined' };
    case 'revoked':
      return { decision: 'revoked' };
    case 'superseded':
      return { decision: 'superseded' };
    case 'expired':
      return { decision: 'expired' };
    default:
      break;
  }

  let failed = false;
  for (const status of facts.deliveries) {
    if (status === 'failed') {
      failed = true;
      continue;
    }
    if (EXISTS.has(status)) return { decision: 'already_has_email' };
    if (status.trim().length > 0) return { decision: 'already_has_email' };
  }
  if (failed) return { decision: 'previous_attempt_failed' };

  const to = normalize(facts.recipientEmail ?? '');
  if (!to) return { decision: 'no_recipient_email' };
  if (!plausible(to)) return { decision: 'recipient_email_invalid' };

  return {
    decision: 'eligible',
    message: facts.viewState === 'pending_acceptance' ? 'claim' : 'notice',
  };
}

export function isFirstSendEligible(v: FirstSendVerdict): boolean {
  return v.decision === 'eligible';
}

/**
 * What the operator reads. One sentence per reason, Turkish, in the product's own voice — and
 * `tests/fixtures/first-send-eligibility.json` holds the same sentences, so a reason cannot be
 * shown by one half and named differently by the other.
 */
export const FIRST_SEND_REASON_TEXT: Record<FirstSendDecision, string> = {
  eligible: 'İlk e-posta gönderilebilir.',
  already_has_email: 'Bu kayıt için zaten bir e-posta var; ilk gönderim yapılamaz.',
  previous_attempt_failed:
    'Önceki gönderim başarısız; bu ilk gönderim değil, mevcut yeniden deneme kurallarıyla ele alınır.',
  no_recipient_email: 'Alıcı e-posta adresi yok.',
  recipient_email_invalid: 'Alıcı e-posta adresi geçerli değil.',
  awaiting_admin_publish: 'Kayıt yönetici yayınını bekliyor; yayından önce e-posta gönderilmez.',
  declined: 'Alıcı reddetti.',
  revoked: 'Kayıt iptal edildi.',
  superseded: 'Kayıt yenisiyle değiştirildi.',
  expired: 'Kaydın süresi doldu.',
};

/*
 * WHAT BECAME OF THE MESSAGE — the delivery's own vocabulary.
 *
 * A send request's target settles at «queued»: handing the message to the outbox is the whole of
 * what the request does, and that word never moves again. The DELIVERY row beside it goes on living
 * — the worker hands it to the provider, the provider accepts it, a webhook confirms it reached the
 * person — and `email_delivery_status` is where that story is told. The result screen therefore
 * leads with the delivery and keeps the request's own word as the operation detail it is.
 *
 * The eight statuses are the database's closed enum (migration 001) and the buckets below are read
 * off the server's own behaviour, not invented here:
 *
 *   - `queued` · `sending`      on its way: not yet accepted by any provider.
 *   - `soft_bounced`            ALSO on its way. `applyTransportFailure` only writes this status
 *                              when it has scheduled a retry (`attemptCount < MAX_ATTEMPTS`);
 *                              once the attempts are spent the status becomes `failed`. So it is a
 *                              retryable setback and must never be shown as a settled failure.
 *   - `sent`                    the provider accepted it. Nobody has confirmed it arrived.
 *   - `delivered`               confirmed. One of the four the server itself calls terminal.
 *   - `hard_bounced` · `failed` · `suppressed`   terminal, and no message reached anybody.
 *
 * A status this table does not know is counted on its own and never folded into a success — the
 * enum is closed, so a ninth value can only arrive with a migration, and until it is learned here
 * the honest answer is «this one cannot be read».
 */
export const DELIVERY_STATUSES = [
  'queued',
  'sending',
  'sent',
  'delivered',
  'soft_bounced',
  'hard_bounced',
  'failed',
  'suppressed',
] as const;

export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

/** Where a delivery stands. `unknown` is a status this product has not learned. */
export type DeliveryBucket = 'on_the_way' | 'accepted' | 'delivered' | 'failed' | 'unknown';

const DELIVERY_BUCKET: Record<DeliveryStatus, DeliveryBucket> = {
  queued: 'on_the_way',
  sending: 'on_the_way',
  soft_bounced: 'on_the_way',
  sent: 'accepted',
  delivered: 'delivered',
  hard_bounced: 'failed',
  failed: 'failed',
  suppressed: 'failed',
};

/** `null` means there is no delivery at all — a target the request did not queue. */
export function deliveryBucketOf(status: string | null): DeliveryBucket | null {
  if (status === null || status.trim().length === 0) return null;
  return DELIVERY_BUCKET[status as DeliveryStatus] ?? 'unknown';
}

/**
 * What the DELIVERY row says, in the product's own words.
 *
 * Nothing here is inferred from the request. A soft bounce names its retry, because a reader who
 * is told only «geri döndü» will reasonably believe the message is gone.
 */
export const DELIVERY_TEXT: Record<DeliveryStatus, string> = {
  queued: 'Gönderim bekliyor',
  sending: 'Gönderiliyor',
  sent: 'Sağlayıcı kabul etti, teslim teyidi bekliyor',
  delivered: 'Teslim edildi',
  soft_bounced: 'Geçici olarak geri döndü, yeniden denenecek',
  hard_bounced: 'Geri döndü, teslim edilemedi',
  suppressed: 'Adres engelli, gönderilmedi',
  failed: 'Gönderilemedi',
};

export function deliveryText(status: string | null): string {
  if (status === null || status.trim().length === 0) return '—';
  return DELIVERY_TEXT[status as DeliveryStatus] ?? `Bilinmeyen durum: ${status}`;
}

export interface DeliverySummary {
  onTheWay: number;
  accepted: number;
  delivered: number;
  failed: number;
  unknown: number;
  /** Targets that really have a delivery row. */
  deliveries: number;
  /** Targets the request did not queue, so they have no delivery to report. */
  withoutDelivery: number;
}

/** Each target lands in exactly one place, and a target without a delivery lands in none of the four. */
export function summariseDeliveries(targets: readonly { deliveryStatus: string | null }[]): DeliverySummary {
  const s: DeliverySummary = {
    onTheWay: 0,
    accepted: 0,
    delivered: 0,
    failed: 0,
    unknown: 0,
    deliveries: 0,
    withoutDelivery: 0,
  };
  for (const t of targets) {
    const bucket = deliveryBucketOf(t.deliveryStatus);
    if (bucket === null) {
      s.withoutDelivery += 1;
      continue;
    }
    s.deliveries += 1;
    if (bucket === 'on_the_way') s.onTheWay += 1;
    else if (bucket === 'accepted') s.accepted += 1;
    else if (bucket === 'delivered') s.delivered += 1;
    else if (bucket === 'failed') s.failed += 1;
    else s.unknown += 1;
  }
  return s;
}

/** The request's own counts, as the server groups its targets. */
export interface OperationCounts {
  pending: number;
  queued: number;
  skipped: number;
  failed: number;
}

/**
 * WHAT THE DEPLOYMENT WILL DO WITH WORK THAT IS STILL WAITING.
 *
 * `undefined` is «this deployment has not said», which is what an older backend's answer looks
 * like, and it is deliberately NOT read as «yes»: an unknown becomes the sentence without the
 * promise rather than the promise without the knowledge.
 */
export interface SendProcessing {
  workerEnabled: boolean;
  sendRestricted: boolean;
}

/**
 * The one sentence at the top of the result.
 *
 * It describes the DELIVERIES once the queuing is done, because that is what the operator came
 * back to find out. «Hepsi teslim edildi» is said only when every queued message really is
 * delivered AND the request left nothing behind: a skipped or failed target is not a success, and a
 * mixed outcome gets its parts named rather than a verdict.
 *
 * THE PENDING SENTENCE IS THE ONE THAT WENT WRONG. It said «gönderim sürer» — the send continues —
 * about every waiting target, and on 17 October that was false: production has run with no worker
 * since the 12 October containment, so the accepted request's one target sat `pending` with
 * `claimed_at` NULL and nothing was ever going to take it. The operator read the promise, waited
 * for an e-mail, and came back to a window that still said the same thing. The promise is now made
 * only when the server says a worker is running, and when it says the opposite the window says the
 * opposite — because «still queued» and «queued in a deployment that is not sending» are the same
 * row and a completely different thing to be told.
 */
export function firstSendHeadline(
  s: DeliverySummary,
  ops: OperationCounts,
  processing?: SendProcessing,
): string {
  if (ops.pending > 0) {
    if (processing?.workerEnabled === false)
      return `${ops.pending} kayıt sırada. Bu dağıtımda gönderim işçisi kapalı: bir yetkili açana kadar hiçbir mesaj gönderilmez. Kayıtlar, kabul bağlantıları ve sıra olduğu gibi durur; sonucu buradan yeniden okunabilir.`;
    if (processing === undefined) return `${ops.pending} kayıt sırada. Sonucu buradan yeniden okunabilir.`;
    return `${ops.pending} kayıt sırada. Bu pencere kapatılsa da gönderim sürer; sonucu buradan yeniden okunabilir.`;
  }
  if (s.deliveries === 0)
    return ops.skipped + ops.failed > 0
      ? 'Hiçbir e-posta kuyruğa alınmadı; işleme alınmayan kayıtlar aşağıda.'
      : 'Hiçbir e-posta kuyruğa alınmadı.';
  const clean = ops.skipped === 0 && ops.failed === 0;
  if (clean && s.delivered === s.deliveries) return `${s.delivered} e-posta teslim edildi.`;
  const parts: string[] = [];
  if (s.delivered > 0) parts.push(`${s.delivered} teslim edildi`);
  if (s.accepted > 0) parts.push(`${s.accepted} sağlayıcı kabul etti, teslim teyidi bekliyor`);
  if (s.onTheWay > 0) parts.push(`${s.onTheWay} gönderim bekliyor`);
  if (s.failed > 0) parts.push(`${s.failed} teslim hatası`);
  if (s.unknown > 0) parts.push(`${s.unknown} durumu okunamadı`);
  if (ops.skipped > 0) parts.push(`${ops.skipped} kayıt işleme alınmadı`);
  if (ops.failed > 0) parts.push(`${ops.failed} kayıt işleme sırasında başarısız oldu`);
  return `Gönderim durumu: ${parts.join(' · ')}.`;
}
